import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  AD_BANNER_AUTO_REFRESH_NEXT_EVENT_NAME,
  AD_BANNER_AUTO_REFRESH_PAUSE_EVENT_NAME,
  AD_BANNER_AUTO_REFRESH_PREV_EVENT_NAME,
  AD_BANNER_AUTO_REFRESH_RESUME_EVENT_NAME,
} from '../../adServerConst'

export interface AdServerBannerNavigationProps {
  bannerRef: RefObject<HTMLDivElement | null>
  carouselRef: RefObject<HTMLElement | null>
  currentIndex: number
  totalCount: number
}

interface AdServerBannerNavigationActions {
  handleBlurButton: () => void
  handleFocusButton: () => void
  handleNextButton: () => void
  handlePauseButton: () => void
  handlePrevButton: () => void
  handleResumeButton: () => void
  isPaused: boolean
}

interface AdServerBannerFocusableElementProps {
  bannerRef: RefObject<HTMLDivElement | null>
  currentIndex: number
  handleBlurButton: () => void
  handleFocusButton: () => void
}

const useAdServerBannerFocusableElement = ({
  bannerRef,
  currentIndex,
  handleBlurButton,
  handleFocusButton,
}: AdServerBannerFocusableElementProps): void => {
  const [focusableElement, setFocusableElement] =
    useState<HTMLAnchorElement | null>(null)

  /**
   * Focus / blur handler for injected ad server banners
   * There are two types of banners:
   * 1. image wrapped in focusable anchor element
   * 2. iframe containing HTML banner including focusable anchor
   */
  useEffect(() => {
    if (!bannerRef.current) {
      return
    }

    // 1. image wrapped in focusable anchor element
    const focusableLink = bannerRef.current.querySelector('a')

    if (focusableLink) {
      setFocusableElement(focusableLink)

      return
    }

    // 2. iframe containing HTML banner including focusable anchor
    const iframe = bannerRef.current.querySelector('iframe')

    if (!iframe) {
      return
    }

    const onIframeLoad = (): void => {
      const focusableIframeLink = iframe.contentDocument?.querySelector('a')

      if (focusableIframeLink) {
        setFocusableElement(focusableIframeLink)
      }
    }

    iframe.addEventListener('load', onIframeLoad)

    return (): void => {
      iframe.removeEventListener('load', onIframeLoad)
    }
  }, [currentIndex, bannerRef])

  useEffect(() => {
    if (!focusableElement) {
      return
    }

    focusableElement.addEventListener('focus', handleFocusButton)
    focusableElement.addEventListener('blur', handleBlurButton)

    return (): void => {
      focusableElement.removeEventListener('focus', handleFocusButton)
      focusableElement.removeEventListener('blur', handleBlurButton)
    }
  }, [focusableElement, handleFocusButton, handleBlurButton])
}

export const useAdServerBannerNavigationActions = ({
  bannerRef,
  carouselRef,
  currentIndex,
  totalCount,
}: AdServerBannerNavigationProps): AdServerBannerNavigationActions => {
  const [isPaused, setIsPaused] = useState(false)
  const isLastSlide = useMemo(
    () => currentIndex + 1 === totalCount,
    [currentIndex, totalCount],
  )

  const dispatchEvent = useCallback(
    (eventName: string) => {
      if (bannerRef.current) {
        bannerRef.current.dispatchEvent(new CustomEvent(eventName))
      }
    },
    [bannerRef],
  )

  const handlePauseButton = useCallback(() => {
    dispatchEvent(AD_BANNER_AUTO_REFRESH_PAUSE_EVENT_NAME)

    setIsPaused(true)
  }, [dispatchEvent])

  const handleResumeButton = useCallback(() => {
    dispatchEvent(AD_BANNER_AUTO_REFRESH_RESUME_EVENT_NAME)

    setIsPaused(false)
  }, [dispatchEvent])

  const handlePrevButton = useCallback(() => {
    dispatchEvent(AD_BANNER_AUTO_REFRESH_PREV_EVENT_NAME)

    if (!isPaused) {
      handlePauseButton()
    }
  }, [dispatchEvent, isPaused, handlePauseButton])

  const handleNextButton = useCallback(() => {
    dispatchEvent(AD_BANNER_AUTO_REFRESH_NEXT_EVENT_NAME)

    if (!isPaused) {
      handlePauseButton()
    }
  }, [dispatchEvent, isPaused, handlePauseButton])

  const handleFocusButton = useCallback(() => {
    if (!isPaused) {
      handlePauseButton()
    }
  }, [isPaused, handlePauseButton])

  const handleBlurButton = useCallback(() => {
    if (!isLastSlide && isPaused) {
      setTimeout(() => {
        if (
          carouselRef.current &&
          !carouselRef.current.contains(document.activeElement)
        ) {
          handleResumeButton()
        }
      }, 0)
    }
  }, [isLastSlide, isPaused, handleResumeButton, carouselRef])

  useEffect(() => {
    if (isLastSlide && !isPaused) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- don't reference, refactor if touched
      handlePauseButton()
    }
  }, [isPaused, isLastSlide, handlePauseButton])

  useAdServerBannerFocusableElement({
    bannerRef,
    currentIndex,
    handleBlurButton,
    handleFocusButton,
  })

  return useMemo(
    () => ({
      handleBlurButton,
      handleFocusButton,
      handleNextButton,
      handlePauseButton,
      handlePrevButton,
      handleResumeButton,
      isPaused,
    }),
    [
      handleBlurButton,
      handleFocusButton,
      handleNextButton,
      handlePauseButton,
      handlePrevButton,
      handleResumeButton,
      isPaused,
    ],
  )
}
